import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

StatusCard.propTypes = {
  compact: PropTypes.bool,
  uploadId: PropTypes.string,
  currentTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentRemaining: PropTypes.number,
  currentRate: PropTypes.number,
  resolution: PropTypes.string,
  mark: PropTypes.number,
  onMark: PropTypes.func,
  onMarkEnd: PropTypes.func,
  onMarkCancel: PropTypes.func,
  onHelp: PropTypes.func,
  clippingMode: PropTypes.bool,
  onToggleClippingMode: PropTypes.func,
  canPlay: PropTypes.bool,
  markDisabledReason: PropTypes.string
}

const Metric = ({ label, value, inline = false }) => inline
  ? (
    // One line, so the transport bar stays a single 44px row on desktop.
    <Box className="editor-transport-metric" sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' }}>
      <Typography component="span" color="text.secondary" sx={{ fontSize: 13, lineHeight: '20px' }}>
        {label}
      </Typography>
      <Typography component="span" className="editor-metric" sx={{ fontSize: 14, fontWeight: 650, lineHeight: '20px' }}>
        {value ?? '—'}
      </Typography>
    </Box>
    )
  : (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1, mb: 0.35, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        className="editor-metric"
        sx={{ fontWeight: 650, lineHeight: 1.1 }}
      >
        {value ?? '—'}
      </Typography>
    </Box>
    )

Metric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inline: PropTypes.bool
}

export default function StatusCard ({
  compact = false,
  uploadId,
  currentTime,
  currentRemaining,
  currentRate,
  resolution,
  mark,
  onMark,
  onMarkEnd,
  onMarkCancel,
  onHelp,
  clippingMode = false,
  onToggleClippingMode,
  canPlay = true,
  markDisabledReason = null
}) {
  // With a reason, the disabled button is wrapped so its tooltip can say why (see the return below).
  const markButton = (
    <Button
      size="small"
      variant="contained"
      startIcon={<BookmarkAddIcon />}
      onClick={onMark}
      disabled={!uploadId || !canPlay || Boolean(markDisabledReason)}
    >
      Mark clip
    </Button>
  )
  return (
    <Paper
      variant="outlined"
      className="editor-inspector-panel"
      sx={compact ? { px: 1, py: 0.5 } : { px: { xs: 1, sm: 1.25 }, py: 0.9 }}
    >
      <Stack
        direction={compact ? 'row' : { xs: 'column', sm: 'row' }}
        useFlexGap
        flexWrap="wrap"
        spacing={1}
        alignItems={compact ? 'center' : { xs: 'stretch', sm: 'center' }}
        sx={compact ? { rowGap: 0.5 } : undefined}
      >
        <Stack direction="row" spacing={0.75} alignItems="center">
          {mark != null
            ? <>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<StopCircleIcon />}
                  onClick={onMarkEnd}
                >
                  Finish clip
                </Button>
                <Tooltip title="Cancel current clip mark">
                  <IconButton size="small" onClick={onMarkCancel}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            : markDisabledReason
              ? <Tooltip title={markDisabledReason} describeChild><span>{markButton}</span></Tooltip>
              : markButton}

          <Tooltip title="Player controls and keyboard shortcuts">
            <IconButton size="small" onClick={onHelp}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider
          flexItem
          orientation="vertical"
          sx={{ display: { xs: 'none', sm: 'block' }, mx: 0.5 }}
        />

        <Stack
          direction="row"
          spacing={compact ? 2 : { xs: 2, md: 3 }}
          useFlexGap
          flexWrap={compact ? 'nowrap' : 'wrap'}
          alignItems="center"
          sx={{ flex: 1 }}
        >
          <Metric inline={compact} label="Playhead" value={currentTime} />
          {!compact && <Metric label="Remaining" value={currentRemaining} />}
          <Metric inline={compact} label="Speed" value={currentRate ? `${currentRate}×` : '1×'} />
          {!compact && <Metric label="Source" value={resolution || '—'} />}
          {mark != null ? <Metric inline={compact} label="Clip in" value={mark} /> : null}
        </Stack>
        {onToggleClippingMode
          ? <Button
              size="small"
              variant="outlined"
              startIcon={clippingMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
              onClick={onToggleClippingMode}
              disabled={!canPlay && !clippingMode}
              aria-pressed={clippingMode}
              aria-keyshortcuts="Meta+Enter Control+Enter"
              sx={{ flexShrink: 0 }}
            >
              {clippingMode ? 'Exit full screen' : 'Full screen'}
            </Button>
          : null}
      </Stack>
    </Paper>
  )
}
